import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { isEmpty, set } from "lodash";
import { Tree, TreeDataNode, TreeProps } from "antd-latest";
import { Checkbox, Icon, Input } from "antd";
import { FaFolderTree } from "react-icons/fa6";
import styles from "../../styles.module.scss";
import { Flex } from "@components/ui/Flex";
import { DataSourceHandler } from "@api/data-source-handler";
import { RootState } from "@store/types";
import { errorHandlerFor400 } from "@api/services/errorhandler";
import { Text } from "@components/ui/Text";
import { ShowWhenTrue } from "@helpers/showwhentrue";
import { FaFolder } from "react-icons/fa";
import { _canvasdagFormInitialValues } from "@pages/workflow-page/utils";
import { _selectoptionType } from "@components/form/select-field";
import { ModelRequestType, RootSearchType, RootType } from "./constant";
import HierarchyView from "./view";
import { TableMetaData } from "@pages/data-browser/treeviews/connections-treeview";
import { InPageSpinner } from "@components/spinners/in-page-spinner";
import { Attribute } from "@pages/admin/asset_heirarchy/types";
import { errorAlert } from "@components/toastify/notify-toast";
import moment from 'moment';
const { Search } = Input;

type Props = {
    activeRecordsMetadata?: TableMetaData;
    formValuesForEdit?: ModelRequestType | null;
    setFormValuesForEdit?: (values: ModelRequestType | null) => void;
    handleGoToReqPage?: () => void;
    hierarchyName?: string;
}

const Hierarchy: React.FC<Props> = ({ activeRecordsMetadata, handleGoToReqPage, formValuesForEdit, setFormValuesForEdit,hierarchyName }) => {
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const [selectedElements, setSelectedElements] = useState<RootType>({} as RootType);
    const [isLoading, setIsLoading] = useState(false);
    const [tagPosition, setTagPosition] = useState("Elements");
    const [isGlobalSearch, setIsGlobalSearch] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
    const [isLoadingElemets, setIsLoadingElements] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [searchTxt, setSearchTxt] = useState("");
    const [filteredData, setFilteredData] = useState<RootType[]>([]);
    const [filteredPipointData, setPipointFilteredData] = useState<RootType[]>([]);
    const [roots, setRoots] = useState<RootType[]>([]);
    const [searchRoots, setSearchRoots] = useState<RootSearchType[]>([]);
    const [pipoints, setPipoints] = useState<Attribute[]>([]);
    const [activeStep, setActiveStep] = useState(0);
    const handleRowSelection = (record: any) => {
        const selectedIndex = selectedRowKeys.indexOf(tagPosition === "Elements" ? record.attr_name : record.pipoint_name);
        const newSelectedKeys = [...selectedRowKeys];

        if (selectedIndex > -1) {
            newSelectedKeys.splice(selectedIndex, 1);
        } else {
            newSelectedKeys.push(tagPosition === "Elements" ? record.attr_name : record.pipoint_name);
        }

        setSelectedRowKeys(newSelectedKeys);
    };

    const handleSearch = (confirm: () => void) => {
        confirm();
    };
    const handleReset = (clearFilters: () => void) => {
        clearFilters();
    };

    const getColumnSearchProps = (dataIndex: string) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
            <div style={{ padding: 8 }}>
                <input
                    className="search_input"
                    placeholder={`Search ${dataIndex}`}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    value={selectedKeys[0]}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Flex gap="10px">
                    <button className="btn btn-yellow btn-sm" style={{ width: 90 }} onClick={() => handleSearch(confirm)}>Search</button>
                    <button className="btn btn-grey btn-sm" style={{ width: 90 }} onClick={() => handleReset(clearFilters)}>Reset</button>
                </Flex>
            </div>
        ),
        onFilter: (value: any, record: any) =>
            record[dataIndex]
                ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase())
                : '',
        filterIcon: (filtered: any) => (
            <Icon type="search" style={{ color: filtered ? '#1890ff' : undefined }} />
        ),
        render: (text: any) => text,
    });

    const mapToTreeData = (
        items: (RootType | RootSearchType)[],
        seenIds = new Set<string>()
    ): TreeDataNode[] => {
        return items.reduce<TreeDataNode[]>((acc, item) => {
            if (seenIds.has(item.elementPath)) {
                return acc;
            }

            seenIds.add(item.elementPath);

            const node: TreeDataNode = {
                title: item.name,
                key: item.elementPath,
                children: item.children ? mapToTreeData(item.children, seenIds) : [],
            };

            acc.push(node);
            return acc;
        }, []);
    };
    
    const getRoots = () => {
        setIsLoading(true)
        DataSourceHandler.GetProjectAssetHierarchyRoots(
            `${Env?.REACT_APP_PLATFORM_URL}/asset/api`, hierarchyName|| "",
            (response) => {
                setRoots(response)
                setIsLoading(false)
            },
            (error: any) => {
                errorHandlerFor400(error)
                setIsLoading(false)
            },
        );
    };

    const getPipoints = () => {
        setIsLoading(true)
        DataSourceHandler.GetPipoints(
            `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`, activeRecordsMetadata?.managedNodeInfo?.nodeName || "",
            activeRecordsMetadata?.afStructureDto?.serverName || "",
            activeRecordsMetadata?.afStructureDto?.databaseName || "",
            (response) => {
                const _pipoints = response.map((item: any) => ({
                    key: item,
                    pipoint_id: item,
                    pipoint_name: item,
                }));
                setPipoints(_pipoints);
                setPipointFilteredData(_pipoints || []);
                setIsLoading(false);
            },
            (error: any) => {
                errorHandlerFor400(error)
                setIsLoading(false)
            },
        );
    };

    const treeData = useMemo(() => mapToTreeData(isGlobalSearch ? searchRoots : roots), [isGlobalSearch, searchRoots, roots]);

    const handleSelect: TreeProps['onSelect'] = (selectedKeys, info) => {
        setIsLoadingElements(true);
        setSelectedElements({} as RootType);
        setFilteredData([]);
        if (info.selected) {
            DataSourceHandler.GetProjectHierarchyAttributes(
                `${Env?.REACT_APP_PLATFORM_URL}/asset/api`,
                info.node.key?.toString() || "", hierarchyName || "",
                (response) => {
                    setSelectedElements(response);
                    setFilteredData(response.attributes || []);
                    setIsLoadingElements(false);
                    // const updateTree = (nodes: RootType[]): RootType[] => {
                    //     return nodes.map((node) => {
                    //         if (node.element_id === info.node.key?.toString()) {
                    //             return { ...node, children: response?.children || [] };
                    //         }
                    //         if (node.children) {
                    //             return { ...node, children: updateTree(node.children) };
                    //         }
                    //         return node;
                    //     });
                    // };

                    // setRoots((prevRoots) => {
                    //     const newRoots = updateTree(prevRoots);
                    //     return [...newRoots];
                    // });
                },
                (error: any) => {
                    errorHandlerFor400(error);
                    setIsLoadingElements(false);
                }
            );
        } else {
            setSelectedElements({} as RootType);
            setFilteredData([]);
            setIsLoadingElements(false);
        }
    };

    const tableDataSources: any = !isGlobalSearch ? useMemo(() => {
        return selectedElements?.attributes?.map((element) => ({
            key: element.name,
            attr_name: element.name,
            tag: element.tag,
        }));
    }, [selectedElements]) : useMemo(() => {
        return searchRoots?.map((element) => ({
            key: element.attr_id,
            attr_name: element.attr_name,
            pipoint_name: element.pipoint_name,
        }));
    }, [searchRoots]);

    const handleSelectAll = () => {
        const _filteredData = tagPosition === "Elements" ? isGlobalSearch ? searchRoots : filteredData : filteredPipointData;
        if (selectedRowKeys.length === _filteredData.length) {
            setSelectedRowKeys([]);
        } else {
            setSelectedRowKeys(_filteredData.map((item: any) => tagPosition === "Elements" ? item.attr_name : item.pipoint_name));
        }
    };

    const columns: any = [
        // {
        //     title: (
        //         <Checkbox
        //             checked={tagPosition === "Elements" ? selectedRowKeys?.length === tableDataSources?.length && tableDataSources?.length > 0 : selectedRowKeys?.length === pipoints?.length && pipoints?.length > 0}
        //             indeterminate={tagPosition === "Elements" ? selectedRowKeys?.length > 0 && selectedRowKeys?.length < tableDataSources?.length : selectedRowKeys?.length > 0 && selectedRowKeys?.length < pipoints?.length}
        //             onChange={handleSelectAll}
        //         />

        //     ),
        //     dataIndex: 'selection',
        //     key: 'selection',
        //     width: 100,
        //     render: (_: any, record: any) => (
        //         <Checkbox
        //             checked={selectedRowKeys.includes(tagPosition === "Elements" ? record.attr_name : record.pipoint_name)}
        //             onChange={() => handleRowSelection(record)}
        //         />
        //     )
        // },
        tagPosition === "Elements" ? {
            title: 'Attribute Name',
            dataIndex: 'attr_name',
            key: 'attr_name',
            sorter: (a: any, b: any) => a.attr_name?.length - b.attr_name?.length,
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('attr_name'),
        } : {
            title: 'PIPoint Name',
            dataIndex: 'pipoint_name',
            key: 'pipoint_name',
            sorter: (a: any, b: any) => a.pipoint_name?.length - b.pipoint_name?.length,
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('pipoint_name'),
        },
        {
            title: 'Value',
            dataIndex: 'tag',
            key: 'tag',
            sorter: (a: any, b: any) => a.tag?.length - b.tag?.length,
            sortDirections: ['descend', 'ascend'],
            ...getColumnSearchProps('tag'),
        },
        //  {
        //             title: 'Created Date',
        //             dataIndex: 'createdDate',
        //             key: 'createdDate',
        //             width: 150,
        //             render: (text: any) => text ? moment(text).format('MM/DD/YYYY HH:mm:ss') : ''
        //         },
        //         {
        //             title: 'Updated Date',
        //             dataIndex: 'updatedDate',
        //             key: 'updatedDate',
        //             width: 150,
        //             render: (text: any) => text ? moment(text).format('MM/DD/YYYY HH:mm:ss') : ''
        //         },
    ];

    const handleTableChange = (pagination: any, filters: any, sorter: any, extra: { action: string; currentDataSource: any; }) => {
        if (tagPosition === "Elements") {
            setFilteredData(extra.currentDataSource);
        } else {
            setPipointFilteredData(extra.currentDataSource);
        }
    };

    const handleSearchAttr = (text: string) => {
        if (!isEmpty(text)) {
            setIsLoading(true)
            DataSourceHandler.GetSearchRoots(
                `${Env?.REACT_APP_PLATFORM_URL}/databrowser/api`, activeRecordsMetadata?.managedNodeInfo?.nodeName || "",
                activeRecordsMetadata?.afStructureDto?.serverName || "",
                activeRecordsMetadata?.afStructureDto?.databaseName || "",
                text,
                (response) => {
                    setIsGlobalSearch(true);
                    setSearchRoots(response)
                    setIsLoading(false)
                },
                (error: any) => {
                    errorHandlerFor400(error)
                    setIsLoading(false)
                },
            );
        } else {
            setIsGlobalSearch(false);
            setSearchRoots([]);
        }

    };

    const requestJson = JSON.parse(formValuesForEdit?.requestJson || `{}`);
    const elementRootPath = requestJson?.element_root;
    const elementPathName = requestJson?.element_name;

    useEffect(() => {
        if (!isEmpty(formValuesForEdit)) {
            if (selectedElements?.path?.replace(/\\/g, "") === `${elementRootPath}${elementPathName}`.replace(/\\/g, "") && formValuesForEdit?.tagSelectedType === "Elements") {
                setSelectedRowKeys(formValuesForEdit?.tagSelections || []);
            } else if (formValuesForEdit?.tagSelectedType === "Pipoint") {
                setSelectedRowKeys(formValuesForEdit?.tagSelections || []);
            }
            else {
                setSelectedRowKeys([]);
            }
            setTagPosition(formValuesForEdit?.tagSelectedType || "Elements");
        }
        if (isEmpty(formValuesForEdit)) {
            setSelectedRowKeys([]);
        }
    }, [formValuesForEdit, selectedElements]);

    useEffect(() => {
        if(!isEmpty(Env)){
            getRoots();
            // getPipoints();
        }
    }, [Env]);

    if (isLoading) {
        return <Flex className={styles.treeContainer} display={"flex"} flexDirection={"column"} alignItems={"center"} justifyContent={"center"}>
            <InPageSpinner size="Large" color="white" />
            <Text>Fetching data ...</Text>
        </Flex>
    }
    return (
        <div>
            <div className={styles.projectTreeContainer}>
                <Flex gap="30px">
                    <ShowWhenTrue show={tagPosition === "Elements"}>
                        <Flex className={styles.boxHierarchyTreeView} flexDirection={"column"} gap="0px">
                            <div className={styles.labelHierarchyView}>
                                <FaFolderTree />
                                <Text fontSize={14} fontWeight={500}>Elements</Text>
                                {/* <div>
                                    <Search
                                        placeholder="Search Attribute name"
                                        allowClear
                                        value={searchTxt}
                                        onChange={(e) => setSearchTxt(e.target.value)}
                                        onSearch={handleSearchAttr}
                                        className={styles["customAttrSearch"]}
                                    />
                                </div> */}
                            </div>
                            <ShowWhenTrue show={!isEmpty(treeData)}>
                                <Tree
                                    className="custom-tree"
                                    selectable
                                    showLine
                                    icon={<FaFolder />}
                                    showIcon
                                    defaultExpandAll
                                    onSelect={handleSelect}
                                    treeData={treeData}
                                />
                            </ShowWhenTrue>
                        </Flex>
                    </ShowWhenTrue>
                    <HierarchyView
                        formValuesForEdit={formValuesForEdit}
                        setFormValuesForEdit={setFormValuesForEdit}
                        selectedElements={selectedElements}
                        tableDataSources={tableDataSources}
                        columns={columns}
                        isLoadingElements={isLoadingElemets}
                        setIsLoadingElements={setIsLoadingElements}
                        showForm={showForm}
                        activeStep={activeStep}
                        pipoints={pipoints}
                        activeRecordsMetadata={activeRecordsMetadata}
                        selectedRowKeys={selectedRowKeys}
                        setActiveStep={setActiveStep}
                        setShowForm={setShowForm}
                        setSelectedRowKeys={setSelectedRowKeys}
                        handleTableChange={handleTableChange}
                        handleGoToReqPage={handleGoToReqPage}
                        tagPosition={tagPosition}
                        isProjectHierarchy={true}
                        setTagPosition={setTagPosition}
                    />
                </Flex>
            </div>

        </div>
    );
};

export default Hierarchy;
